import { useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { ColumnHistoryView } from "../../../components/audit/columnHistory/ColumnHistoryView";
import {
  useColumnHistory,
  type ColumnVersionSnapshot,
} from "../../../hooks/useColumnHistory";
import { useWorkspaceMembers } from "../../../hooks/useWorkspaceMembers";
import type {
  CollabDraftAttributeTestColumn as DraftAttributeTestColumn,
  CollabDraftSupportColumn as DraftSupportColumn,
  CollabProposedAttribute as ProposedAttribute,
} from "../../../models/audit/collabPlanTypes";
import {
  draftAttrTestToProcedureColumn,
  draftSupportToProcedureColumn,
  makeResolveAttributeName,
} from "./columnHistoryRestore";

export type HistoryColumnTarget =
  | { kind: "attribute_test"; column: DraftAttributeTestColumn }
  | { kind: "support"; column: DraftSupportColumn };

/**
 * Version history for one draft-plan column in the revise wizard.
 *
 * Reuses the workpaper editor's `ColumnHistoryView` (version rail + field
 * diff) against a `ProcedureColumn` synthesized from the draft column. The
 * history itself comes from the persisted `procedure_column_history` rows via
 * the existing history endpoint — the draft column's definition id is enough,
 * the endpoint resolves it to the wrapper row.
 *
 * Restore hands the chosen snapshot back to the parent, which applies it as a
 * normal `update_testing_tables` plan patch scoped to this column — so a
 * restore is undoable in-session and persists nothing until Save Changes.
 *
 * The rail also gets a synthetic "Saved" entry — the wrapper's live persisted
 * state from the history response. Diffing it against the draft surfaces this
 * session's unsaved edits (the default view on open, since it's newest), and
 * restoring it discards the session's edits for just this column. Purely a
 * display-side construct: nothing is written until Save Changes, and Abandon
 * Changes is unaffected.
 */
export const ColumnHistoryDialog = ({
  procedureId,
  target,
  proposedAttributes,
  onRestore,
  onClose,
}: {
  procedureId: string;
  target: HistoryColumnTarget;
  proposedAttributes: ProposedAttribute[];
  onRestore: (snapshot: ColumnVersionSnapshot) => void;
  onClose: () => void;
}) => {
  const { loading, history, savedColumn } = useColumnHistory(
    procedureId,
    target.column.id ?? null,
  );

  // The "Saved" baseline slots in as the newest rail entry, one version above
  // the newest real snapshot. Its number is only a selection key — the rail
  // renders it via versionLabel below.
  const savedVersion =
    savedColumn !== null ? (history[0]?.version ?? -1) + 1 : null;
  const railHistory = useMemo(() => {
    if (savedColumn === null || savedVersion === null) return history;
    return [
      {
        id: "saved-baseline",
        columnId: savedColumn.id,
        version: savedVersion,
        columnType: savedColumn.columnType,
        snapshot: savedColumn,
        changedBy: "system",
        createdAt: savedColumn.updatedAt,
      },
      ...history,
    ];
  }, [savedColumn, savedVersion, history]);

  // Lazy-loaded here (dialog mounts only while open) to label version authors.
  const { users } = useWorkspaceMembers({ fetchOnLoad: true });
  const resolveChangedBy = useCallback(
    (changedBy: string) =>
      users.find((u) => u.id === changedBy)?.email ?? changedBy,
    [users],
  );

  const currentColumn = useMemo(
    () =>
      target.kind === "attribute_test"
        ? draftAttrTestToProcedureColumn(target.column, proposedAttributes)
        : draftSupportToProcedureColumn(target.column),
    [target, proposedAttributes],
  );

  const columnLabel =
    (target.kind === "attribute_test"
      ? (target.column.name ?? target.column.attributeReference)
      : target.column.name) ?? "Column";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-5xl flex-col">
        <DialogHeader>
          <DialogTitle>{columnLabel}</DialogTitle>
        </DialogHeader>
        <ColumnHistoryView
          currentColumn={currentColumn}
          history={railHistory}
          loading={loading}
          onBackToEdit={onClose}
          onRestore={onRestore}
          resolveAttributeName={makeResolveAttributeName(proposedAttributes)}
          resolveChangedBy={resolveChangedBy}
          // The wizard's draft columns have no required-documents picker, so
          // hide that diff row (mirrors the editor's gate).
          hasDocumentCategories={false}
          // Draft support columns can't represent the attribute link at all —
          // diffing it would flag a phantom "changed" on every version, and
          // restore couldn't clear it. Attribute-test drafts model it via
          // attribute_reference, so they keep the row.
          hasAssignedAttribute={target.kind !== "support"}
          versionLabel={(version) =>
            version === savedVersion ? "Saved" : `v${version}`
          }
        />
      </DialogContent>
    </Dialog>
  );
};
